import React, { useCallback, useMemo, useState } from 'react';
import styles from './SongDetailsModal.module.css';
import ModalRight from '../../../../SharedComponents/ModalRight/ModalRight';
import {
    getSongAudioDataRefreshRequests,
    getSongLatestTiktokSearchLog,
    getSongTiktokSearchTerms,
    getSongTiktokStatsById,
    refreshSongAudioData,
    removeTiktokAudioFromSong,
    SongAudioDataRefreshRequest,
    SongTiktokStats,
} from '@round/api';
import Image from '../../../../ui/Image/Image';
import CloseIcon from '../../../../SharedComponents/svg/Icons/CloseIcon';
import { ReactComponent as MusicIcon } from '../../../../assets/MusicIcon.svg';
import MetricCard from '../../../../ui/DataDisplay/Statistics/MetricCard/MetricCard';
import MusicNoteIcon from '../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import VideoCameraIcon from '../../../../SharedComponents/svg/Icons/VideoCameraIcon';
import CalendarMonthIcon from '../../../../SharedComponents/svg/Icons/CalendarMonthIcon';
import TopTiktokAudiosTable from './TopTiktokAudios/TopTiktokAudiosTable';
import TopTiktokAudioPosts from './TopTiktokAudioPosts/TopTiktokAudioPosts';
import { ProtectedByUserGroups } from '../../../Auth/components/ProtectedByUserGroup/ProtectedByUserGroups';
import Button from '../../../../ui/Buttons/Button/Button';
import useTopTiktokAudios from './TopTiktokAudios/useTopTiktokAudios';
import useTopTiktokAudioPosts from './TopTiktokAudioPosts/useTopTiktokAudioPosts';
import { formatDate, getLastUpdatedLabel, showNotification } from '../../../../helpers';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import { ReactComponent as RefreshIcon } from '../../../../assets/Refresh.svg';
import cn from 'classnames';
import { useCheckUserGroupsAccess } from '../../../Auth/hooks/useCheckUserGroupsAccess';
import AddSongToSongCollection from '../../../SongCollection/features/AddSongToSongCollection/AddSongToSongCollection';
import { SongCollectionsListContextProvider } from '../../../SongCollection/contexts/SongCollectionsListContext';
import SongCreationsGraph from './SongCreationsGraph/SongCreationsGraph';
import { ConfirmationModal } from '@round/ui-kit';

type SongDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    songId: number | null;
    songTiktokStats?: SongTiktokStats | null;
};

const REFRESH_COOLDOWN = 1000 * 60 * 60;
const SongDetailsModal = ({ onClose, isOpen, songId, songTiktokStats }: SongDetailsModalProps) => {
    const hasUserAdminPermissions = useCheckUserGroupsAccess(['user_admin']);
    const [songStats, setSongStats] = useState<SongTiktokStats | null>(null);
    const { refresh: refreshTopTiktokAudiosTable, getTopTiktokAudiosTableProps } = useTopTiktokAudios(songId);
    const { refresh: refreshTopTiktokAudioPostsTable, getTopTiktokAudioPostsTableProps } =
        useTopTiktokAudioPosts(songId);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [refreshRequests, setRefreshRequests] = useState<SongAudioDataRefreshRequest[]>([]);
    const [refreshRequestsLoading, setRefreshRequestsLoading] = useState(false);

    const [audioDetectionEnabled, setAudioDetectionEnabled] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [audioToRemoveId, setAudioToRemoveId] = useState<number | null>(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const lastRefreshRequestedDate = useMemo(() => {
        if (!refreshRequests.length) {
            return null;
        }

        return refreshRequests
            .map((request) => new Date(request.timestamp))
            .sort((a, b) => b.getTime() - a.getTime())[0];
    }, [refreshRequests]);

    const lastRefreshRequestedDateText = useMemo(() => {
        if (!lastRefreshRequestedDate) {
            return 'never';
        }

        return getLastUpdatedLabel(lastRefreshRequestedDate, REFRESH_COOLDOWN);
    }, [lastRefreshRequestedDate]);

    const isRefreshButtonDisabled =
        refreshLoading ||
        refreshRequestsLoading ||
        Date.now() - (lastRefreshRequestedDate?.getTime() ?? 0) <= REFRESH_COOLDOWN;

    const fetchRefreshRequests = useCallback(async (songId: number, requestInit?: RequestInit) => {
        try {
            setRefreshRequestsLoading(true);
            const response = await getSongAudioDataRefreshRequests({ song_id: songId }, requestInit);
            setRefreshRequests(response.data.results);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
        } finally {
            setRefreshRequestsLoading(false);
        }
    }, []);

    const refreshData = useCallback(async () => {
        if (songId === null) {
            return;
        }

        try {
            setRefreshLoading(true);
            const refreshResponse = await refreshSongAudioData(songId);
            if (refreshResponse.status === 404) {
                showNotification('Could not find a song to refresh', 'error');
                return;
            }

            const songStatsResponse = await getSongTiktokStatsById(songId);
            if (songStatsResponse.status === 404) {
                showNotification('Could not find a song to refresh', 'error');
                return;
            }

            setSongStats(songStatsResponse.data);
            await Promise.all([
                fetchRefreshRequests(songId),
                refreshTopTiktokAudiosTable(),
                refreshTopTiktokAudioPostsTable(),
            ]);

            showNotification('Data refreshed', 'info');
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification('Could not refresh data', 'error');
        } finally {
            setRefreshLoading(false);
        }
    }, [fetchRefreshRequests, refreshTopTiktokAudioPostsTable, refreshTopTiktokAudiosTable, songId]);

    const getMonitoringData = useCallback(async (songId: number, requestInit?: RequestInit) => {
        try {
            const latestSearchLogResponse = await getSongLatestTiktokSearchLog(songId, requestInit);
            const searchTermsResponse = await getSongTiktokSearchTerms(songId, { page: 1, page_size: 1 });
            setLastUpdated(latestSearchLogResponse.data.created);
            setAudioDetectionEnabled(searchTermsResponse.data.count > 0);
        } catch {
            // no-op
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (songTiktokStats) {
                setSongStats(songTiktokStats);
                return;
            }

            if (typeof songId === 'number') {
                getSongTiktokStatsById(songId, { signal })
                    .then((response) => {
                        if (response.status === 200) {
                            setSongStats(response.data);
                        }
                    })
                    .catch(() => {});
            }
        },
        [songTiktokStats, songId]
    );

    useAbortableEffect(
        (signal) => {
            if (typeof songId === 'number' && hasUserAdminPermissions) {
                fetchRefreshRequests(songId, { signal });
            }
        },
        [fetchRefreshRequests, songId, hasUserAdminPermissions]
    );

    useAbortableEffect(
        (signal) => {
            if (typeof songId === 'number') {
                getMonitoringData(songId, { signal });
            }
        },
        [songId, getMonitoringData]
    );

    const handleRemoveAudio = useCallback(
        async (audioId: number | null) => {
            if (typeof songId !== 'number' || typeof audioId !== 'number') {
                return;
            }

            try {
                const response = await removeTiktokAudioFromSong(songId, audioId);
                if (response.status === 204) {
                    refreshTopTiktokAudiosTable();
                    refreshTopTiktokAudioPostsTable();
                    showNotification('Audio removed', 'info');
                    return;
                }

                if (response.status === 404) {
                    showNotification(response.data.detail, 'error');
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }
            } catch {
                showNotification('Could not remove audio', 'error');
            } finally {
                setIsConfirmModalOpen(false);
                setAudioToRemoveId(null);
            }
        },
        [refreshTopTiktokAudioPostsTable, refreshTopTiktokAudiosTable, songId]
    );

    return (
        <ModalRight isModalOpen={isOpen} closeModal={onClose} className={styles.modal}>
            <div className={styles.container}>
                <button className={styles.closeButton} onClick={onClose}>
                    <CloseIcon width={16} height={16} />
                </button>
                <div className={styles.header}>
                    <div className={styles.songContainer}>
                        <Image
                            className={styles.songImage}
                            src={songStats?.song.image ?? ''}
                            alt={songStats?.song.title}
                        />
                        <div className={styles.songInfoContainer}>
                            <a
                                className={cn(styles.songTitle, {
                                    [styles.songActive]: songStats?.song.spotify_song_url,
                                })}
                                href={songStats?.song.spotify_song_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {songStats?.song.title}
                            </a>
                            <span className={styles.songAuthors}>
                                <MusicIcon className={styles.musicIcon} />
                                {songStats?.song.artists.map((a) => a.name).join(', ')}
                            </span>
                        </div>
                    </div>

                    <ProtectedByUserGroups groups={['user_admin']}>
                        <div className={styles.monitoringContainer}>
                            <table className={styles.monitoringInfoTable}>
                                <tr className={styles.monitoringEnabled}>
                                    <th>Audio detection:</th>
                                    <td>
                                        <span
                                            className={cn(styles.monitoringEnabledValue, {
                                                [styles.enabled]: audioDetectionEnabled,
                                                [styles.disabled]: !audioDetectionEnabled,
                                            })}
                                        >
                                            {audioDetectionEnabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </td>
                                </tr>
                                {typeof lastUpdated === 'string' && (
                                    <tr className={styles.monitoringLastUpdated}>
                                        <th>Last updated:</th>
                                        <td>
                                            <span className={styles.monitoringLastUpdatedValue}>
                                                {formatDate(lastUpdated)}
                                            </span>
                                        </td>
                                    </tr>
                                )}
                            </table>

                            {songStats && (
                                <SongCollectionsListContextProvider>
                                    <AddSongToSongCollection
                                        songId={songStats.song.id}
                                        collectionIds={songStats.song_collection_ids}
                                        renderAnchor={({ setRef }) => (
                                            <Button
                                                ref={setRef}
                                                className={cn(styles.actionButton, {
                                                    [styles.disabled]: isRefreshButtonDisabled,
                                                })}
                                                contentClassName={styles.actionButtonContent}
                                            >
                                                + Add to collection
                                            </Button>
                                        )}
                                    />
                                </SongCollectionsListContextProvider>
                            )}

                            <Button
                                hint={`Last updated: ${lastRefreshRequestedDateText}`}
                                disabled={isRefreshButtonDisabled}
                                onClick={refreshData}
                                className={cn(styles.actionButton, { [styles.disabled]: isRefreshButtonDisabled })}
                                contentClassName={styles.actionButtonContent}
                            >
                                <RefreshIcon className={cn(styles.actionIcon, { [styles.spin]: refreshLoading })} />
                                Refresh Data
                            </Button>
                        </div>
                    </ProtectedByUserGroups>
                </div>

                <div className={styles.headlineStats}>
                    <MetricCard
                        baseColor="#0090FF"
                        value={songStats?.audio_count}
                        label="Total audios tracked"
                        Icon={MusicNoteIcon}
                    />
                    <MetricCard
                        baseColor="#F7685B"
                        value={songStats?.total_video_count}
                        label="Total video creations"
                        Icon={VideoCameraIcon}
                    />
                    <MetricCard
                        baseColor="#FFA114"
                        value={songStats?.total_video_count_weekly_change}
                        label="creations in the last 7 days"
                        Icon={CalendarMonthIcon}
                    />
                    <MetricCard
                        baseColor="#48D98A"
                        value={songStats?.total_video_count_daily_change}
                        label="daily creations"
                        Icon={CalendarMonthIcon}
                    />
                </div>

                <SongCreationsGraph data={songStats?.standardised_data} />

                <TopTiktokAudiosTable
                    {...getTopTiktokAudiosTableProps()}
                    onRemoveAudioClicked={(audioId: number) => {
                        setAudioToRemoveId(audioId);
                        setIsConfirmModalOpen(true);
                    }}
                />
                <TopTiktokAudioPosts {...getTopTiktokAudioPostsTableProps()} />
                <ConfirmationModal
                    isOpen={isConfirmModalOpen}
                    onClose={() => {
                        setIsConfirmModalOpen(false);
                        setAudioToRemoveId(null);
                    }}
                    handleConfirm={() => handleRemoveAudio(audioToRemoveId)}
                    modalTitle="Unlink Audio?"
                    modalText="Are you sure you want to unlink this audio?"
                    confirmButtonText="Unlink"
                />
            </div>
        </ModalRight>
    );
};

export default SongDetailsModal;
